import type { FC } from 'react';
import { useContext, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ITabOption, Modal, Tabs } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { ClientCard, Comments } from 'components';
import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';
import { GET_USER_ID, IGetUserIdRequest, IGetUserIdResponse } from 'schemas';

import s from './ClientModal.module.scss';
import { IClientModalProps, TTabType } from './interfaces';
import { PausableTariff } from './PausableTariff';
import { Profile } from './Profile';

export const ClientModal: FC<IClientModalProps> = ({
  clientUUID,
  onClose,
  setIsDepositOpen,
  setIsDefaultClient,
  setIsResetPasswordModal,
  setCurrentClient,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const tabs: Array<ITabOption<TTabType>> = [
    {
      value: 'PROFILE',
      title: t('client.card.tabs.profile'),
    },
    {
      value: 'COMMENTS',
      title: t('client.card.tabs.comments'),
      disabled: !permissions?.has('comment_read'),
    },
    {
      value: 'PAUSABLE_TARIFF',
      title: t('client.card.tabs.pausable_tariff'),
    },
  ];

  const { data: clientResponse } = useQuery<
    IGetUserIdResponse,
    IGetUserIdRequest
  >(GET_USER_ID, {
    fetchPolicy: 'cache-first',
    skip: !permissions?.has('client_info'),
    variables: { uuid: clientUUID },
  });

  const client = clientResponse?.user;

  const [currentTab, setCurrentTab] = useState<TTabType>('PROFILE');

  return (
    <Modal onClose={onClose} className={s.container} disableOutsideClose>
      <Tabs
        options={tabs}
        value={currentTab}
        onChange={setCurrentTab}
        className={s.tabs}
      />
      <div className={s.content}>
        <ClientCard
          clientUUID={clientUUID}
          setIsDepositOpen={setIsDepositOpen}
          isInfoOpened
          setIsDefaultClient={setIsDefaultClient}
          notShowButtonDeposit={false}
          hidePausableTariff
          setIsResetPasswordModal={setIsResetPasswordModal}
          setCurrentClient={setCurrentClient}
          showResetPassword={true}
        />
        <Tabs
          options={tabs}
          value={currentTab}
          onChange={setCurrentTab}
          className={s.tabs_mobile}
        />
        {currentTab === 'PROFILE' && (
          <FeatureHOC
            alias="user_history"
            strategy="replace"
            name={t('client.card.payments.history')}
            grayBG
          >
            <Profile clientUUID={clientUUID} />
          </FeatureHOC>
        )}
        {client &&
          currentTab === 'COMMENTS' &&
          permissions?.has('comment_read') && (
            <FeatureHOC
              alias="user_comments"
              strategy="replace"
              name={t('client.card.comments')}
              grayBG
            >
              <Comments entityId={client.id} type="CLIENT" />
            </FeatureHOC>
          )}
        {client && currentTab === 'PAUSABLE_TARIFF' && (
          <PausableTariff clientUUID={clientUUID} />
        )}
      </div>
    </Modal>
  );
};
